import {
  type CardRect,
  cornerRadius,
  loopbackMargin,
} from "../../lib/routeConnectors";

export type StepMapDensity = "normal" | "medium" | "small";

export type StepMapSpec = {
  cardWidth: number;
  maxCardWidth: number;
  cardHeight: number;
  colGap: number;
  rowGap: number;
  padding: number;
  paddingX: number;
  cardRadius: number;
};

export const DENSITY_SPECS: Record<StepMapDensity, StepMapSpec> = {
  normal: {
    cardWidth: 180,
    maxCardWidth: 280,
    cardHeight: 48,
    colGap: 28,
    rowGap: 8,
    padding: 32,
    paddingX: 32,
    cardRadius: 12,
  },
  medium: {
    cardWidth: 132,
    maxCardWidth: 200,
    cardHeight: 28,
    colGap: 24,
    rowGap: 6,
    padding: 32,
    paddingX: 32,
    cardRadius: 10,
  },
  small: {
    cardWidth: 80,
    maxCardWidth: 80,
    cardHeight: 24,
    colGap: 16,
    rowGap: 4,
    padding: 32,
    paddingX: 32,
    cardRadius: 8,
  },
};

export type SpacingLevel = "compact" | "cosy" | "spacious";

// Each level opens the gaps by a fixed amount without touching card sizes, so
// the spacing toggle only ever gives connectors more room to breathe. The
// steps are spread wide apart so each one reads as a real change.
const SPACING_GAP_BUMP: Record<
  SpacingLevel,
  { colGap: number; rowGap: number }
> = {
  compact: { colGap: 0, rowGap: 0 },
  cosy: { colGap: 28, rowGap: 16 },
  spacious: { colGap: 60, rowGap: 34 },
};

export function spaciousSpecFor(
  spec: StepMapSpec,
  level: SpacingLevel
): StepMapSpec {
  const bump = SPACING_GAP_BUMP[level];
  return {
    ...spec,
    colGap: spec.colGap + bump.colGap,
    rowGap: spec.rowGap + bump.rowGap,
  };
}

// A density's cards at a given spacing level.
export function specForSpacing(
  density: StepMapDensity,
  level: SpacingLevel
): StepMapSpec {
  return spaciousSpecFor(DENSITY_SPECS[density], level);
}

type StepMapLayout = Map<string, { row: number; col: number }>;

type LayoutLink = {
  fromStepId: string;
  toStepId: string;
};

export type StepMapGeometry = {
  rects: Map<string, CardRect>;
  width: number;
  height: number;
  band: {
    rowCount: number;
    workflowLabel: { left: number; top: number };
    standaloneLabel: { left: number; top: number };
  } | null;
};

export type StepMapFit = {
  density: StepMapDensity;
  spec: StepMapSpec;
  geometry: StepMapGeometry;
};

const loopExtra = loopbackMargin + cornerRadius;

// Loopback connectors route through a channel outside the flow's cards (see
// routeConnectors), so any side a loopback passes needs extra space to keep
// the channel clear of canvas edges, section labels, and the standalone band.
function detectLoops(
  layout: StepMapLayout,
  links: readonly LayoutLink[]
): { above: boolean; below: boolean; left: boolean; right: boolean } {
  const cells = Array.from(layout.values());
  const colCount = Math.max(...cells.map((p) => p.col), -1) + 1;

  let above = false;
  let below = false;
  let right = false;
  let left = false;

  for (const link of links) {
    const src = layout.get(link.fromStepId);
    const tgt = layout.get(link.toStepId);
    if (!src || !tgt) continue;

    if (tgt.col <= src.col) {
      if (src.row <= tgt.row) above = true;
      else below = true;
      if (src.col === colCount - 1) right = true;
      if (tgt.col === 0) left = true;
    }
  }

  return { above, below, left, right };
}

export const SECTION_LABEL_SPACE = 24;
export const BAND_FLOW_GAP = 16;
const COMPACT_PANE_HEIGHT = 240;
export const COMPACT_PADDING_Y = 12;

// The standalone band is the contiguous run of top layout rows whose steps
// carry no links (layoutWorkflowSteps packs them there). When a linked flow
// exists it renders as its own labeled shelf BELOW the flow — the flow is the
// main content — with loopback channels routed in the gap between the two.
function detectBandRowCount(
  layout: StepMapLayout,
  links: readonly LayoutLink[]
): number {
  const linked = new Set<string>();
  for (const link of links) {
    if (layout.has(link.fromStepId)) linked.add(link.fromStepId);
    if (layout.has(link.toStepId)) linked.add(link.toStepId);
  }
  if (linked.size === 0) return 0;

  const rowHasLinked = new Map<number, boolean>();
  for (const [id, pos] of layout) {
    rowHasLinked.set(
      pos.row,
      (rowHasLinked.get(pos.row) ?? false) || linked.has(id)
    );
  }
  let band = 0;
  while (rowHasLinked.get(band) === false) band++;
  return band;
}

function computeSize(
  layout: StepMapLayout,
  links: readonly LayoutLink[],
  spec: StepMapSpec,
  paneWidth?: number,
  paneHeight?: number
): {
  padY: number;
  padLeft: number;
  cardWidth: number;
  width: number;
  height: number;
  bandRowCount: number;
  standaloneLabelTop: number;
  rowTop: (row: number) => number;
} {
  const cells = Array.from(layout.values());
  const rowCount = Math.max(...cells.map((p) => p.row), -1) + 1;
  const colCount = Math.max(...cells.map((p) => p.col), -1) + 1;
  const loops = detectLoops(layout, links);
  const bandRowCount = detectBandRowCount(layout, links);
  const pitch = spec.cardHeight + spec.rowGap;
  // A single row needs no vertical breathing room, and short panes spend their
  // height on cards, not margins — both tuck the cards against tight padding.
  const padY =
    rowCount === 1 ||
    (paneHeight !== undefined && paneHeight < COMPACT_PANE_HEIGHT)
      ? COMPACT_PADDING_Y
      : spec.padding;

  const padLeft = loops.left ? spec.paddingX + loopExtra : spec.paddingX;
  const padRight = loops.right ? spec.paddingX + loopExtra : spec.paddingX;
  // Cards soak up surplus pane width, capped so a wide pane doesn't turn a
  // flow into full-width bars; a pane narrower than the base width scrolls
  // rather than shrinking cards. A single step keeps its base width — stretching
  // a lone card just strands an oversized bar in an empty pane.
  const chrome =
    padLeft + padRight + (colCount > 0 ? (colCount - 1) * spec.colGap : 0);
  const cardWidth =
    paneWidth !== undefined && colCount > 0 && layout.size > 1
      ? Math.min(
          spec.maxCardWidth,
          Math.max(spec.cardWidth, Math.floor((paneWidth - chrome) / colCount))
        )
      : spec.cardWidth;
  const width = chrome + (colCount > 0 ? colCount * cardWidth : 0);

  if (bandRowCount === 0) {
    const padTop = loops.above ? padY + loopExtra : padY;
    const padBottom = loops.below ? padY + loopExtra : padY;
    const rowTop = (row: number) => padTop + row * pitch;
    const height =
      (rowCount > 0 ? rowTop(rowCount - 1) + spec.cardHeight : padTop) +
      padBottom;
    return {
      padY,
      padLeft,
      cardWidth,
      width,
      height,
      bandRowCount,
      standaloneLabelTop: 0,
      rowTop,
    };
  }

  const flowTop = padY + SECTION_LABEL_SPACE + (loops.above ? loopExtra : 0);
  const flowRowCount = rowCount - bandRowCount;
  const flowBottom = flowTop + flowRowCount * pitch - spec.rowGap;
  const standaloneLabelTop =
    flowBottom + BAND_FLOW_GAP + (loops.below ? loopExtra : 0);
  const bandTop = standaloneLabelTop + SECTION_LABEL_SPACE;
  const rowTop = (row: number) =>
    row < bandRowCount
      ? bandTop + row * pitch
      : flowTop + (row - bandRowCount) * pitch;
  const height = bandTop + bandRowCount * pitch - spec.rowGap + padY;
  return {
    padY,
    padLeft,
    cardWidth,
    width,
    height,
    bandRowCount,
    standaloneLabelTop,
    rowTop,
  };
}

export function computeStepMapGeometry(
  layout: StepMapLayout,
  links: readonly LayoutLink[],
  spec: StepMapSpec,
  paneWidth?: number,
  paneHeight?: number
): StepMapGeometry {
  const {
    padY,
    padLeft,
    cardWidth,
    width,
    height,
    bandRowCount,
    standaloneLabelTop,
    rowTop,
  } = computeSize(layout, links, spec, paneWidth, paneHeight);
  const rects = new Map<string, CardRect>();
  for (const [id, pos] of layout) {
    const left = padLeft + pos.col * (cardWidth + spec.colGap);
    const top = rowTop(pos.row);
    rects.set(id, {
      id,
      col: pos.col,
      row: pos.row,
      left,
      right: left + cardWidth,
      top,
      bottom: top + spec.cardHeight,
      centerY: top + spec.cardHeight / 2,
    });
  }
  return {
    rects,
    width,
    height,
    band:
      bandRowCount > 0
        ? {
            rowCount: bandRowCount,
            workflowLabel: { left: padLeft, top: padY },
            standaloneLabel: { left: padLeft, top: standaloneLabelTop },
          }
        : null,
  };
}

export function fitStepMap(
  layout: StepMapLayout,
  links: readonly LayoutLink[],
  pane: { width: number; height: number },
  spacing: SpacingLevel = "compact"
): StepMapFit {
  // Density is always picked from the compact layout, so the spacing toggle
  // never shrinks cards to fit — it only opens the gaps (and the pane grows to
  // match, scrolling if it hits its cap).
  const build = (density: StepMapDensity): StepMapFit => {
    const spec = specForSpacing(density, spacing);
    return {
      density,
      spec,
      geometry: computeStepMapGeometry(
        layout,
        links,
        spec,
        pane.width,
        pane.height
      ),
    };
  };
  for (const density of ["normal", "medium"] as const) {
    const { width, height } = computeSize(
      layout,
      links,
      DENSITY_SPECS[density],
      undefined,
      pane.height
    );
    // Medium keeps labels readable in tall maps without adding horizontal scrolling.
    if (
      width <= pane.width &&
      (height <= pane.height || density === "medium")
    ) {
      return build(density);
    }
  }
  return build("small");
}
